import React, { useEffect, useRef, useMemo } from "react";
import { Card, Row, Col, Statistic, Typography } from "antd";
import Chart from "chart.js/auto";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DollarOutlined, ShoppingOutlined } from '@ant-design/icons';

// Register the datalabels plugin
Chart.register(ChartDataLabels);

const SalesTypeChart = ({ data }) => {
  const chartRef = useRef(null);

  // Process the data from the backend or use default sample data
  const salesTypeData = useMemo(() => {
    // Default sample data if no data is provided
    const defaultData = {
      labels: ["Phone", "Mail", "Web", "Unknown"],
      values: [25000, 52000, 23000, 5000],
      orders: [250, 520, 230, 50],
      avgOrderValues: [100, 100, 100, 100],
      colors: ["#6f42c1", "#dc3545", "#ffc107", "#17a2b8"]
    };
    
    // If no data is provided, return the default data
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.log('Using default sales type data');
      return defaultData;
    }
    
    try {
      // Extract labels, values, and assign colors from the backend data
      const labels = data.map(item => {
        // Capitalize first letter of type
        const type = item.type || 'unknown';
        return type.charAt(0).toUpperCase() + type.slice(1);
      });
      
      const values = data.map(item => item.value || 0);
      const orders = data.map(item => item.orders || 0);
      const avgOrderValues = data.map(item => item.avgOrderValue || 0);
      
      // Assign colors based on the type or use a predefined color map
      const colorMap = {
        'phone': '#6f42c1',
        'mail': '#dc3545',
        'web': '#ffc107',
        'unknown': '#17a2b8'
      };
      
      const colors = data.map(item => colorMap[item.type] || '#6c757d');
      
      return { labels, values, orders, avgOrderValues, colors };
    } catch (error) {
      console.error('Error processing sales type data:', error);
      return defaultData;
    }
  }, [data]);

  useEffect(() => {
    if (chartRef.current) {
      const existingChart = Chart.getChart(chartRef.current);
      if (existingChart) existingChart.destroy();

      const ctx = chartRef.current.getContext("2d");
      
      new Chart(ctx, {
        type: "pie",
        plugins: [ChartDataLabels],
        data: {
          labels: salesTypeData.labels,
          datasets: [
            {
              data: salesTypeData.values,
              backgroundColor: salesTypeData.colors,
              borderWidth: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false // Hide the legend since we're showing it below
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const label = context.label || '';
                  const value = context.raw || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = Math.round((value / total) * 100);
                  return `${label}: ${percentage}%`;
                }
              }
            },
            datalabels: {
              display: true,
              color: '#fff',
              font: {
                weight: 'bold',
                size: 12
              },
              formatter: (value, ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = Math.round((value / total) * 100);
                return percentage > 5 ? `${percentage}%` : '';
              }
            }
          }
        }
      });
    }
  }, [salesTypeData]);

  return (
    <Card title="SALES TYPE" className="dashboard-card">
      <Row gutter={[16, 16]}>
        {salesTypeData.labels.map((label, index) => (
          <Col key={index} xs={24} sm={12} md={12} lg={12}>
            <Card
              className="sales-type-card"
              size="small"
              style={{ 
                borderLeft: `4px solid ${salesTypeData.colors[index]}`,
                height: '100%'
              }}
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ 
                    width: "12px", 
                    height: "12px", 
                    backgroundColor: salesTypeData.colors[index],
                    marginRight: "8px",
                    borderRadius: "2px"
                  }}></div>
                  <Typography.Title level={5} style={{ margin: 0 }}>{label}</Typography.Title>
                </div>
              }
            >
              <Row gutter={[8, 16]}>
                <Col span={12}>
                  <Statistic 
                    title="Orders"
                    value={salesTypeData.orders[index]}
                    prefix={<ShoppingOutlined />}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="% of Orders"
                    value={(() => {
                      const totalOrders = salesTypeData.orders.reduce((sum, orders) => sum + orders, 0);
                      return totalOrders > 0 ? ((salesTypeData.orders[index] / totalOrders) * 100).toFixed(1) : 0;
                    })()}
                    suffix="%"
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="Total Sales"
                    value={salesTypeData.values[index]}
                    precision={2}
                    prefix={<DollarOutlined />}
                    valueStyle={{ color: '#3f8600', fontSize: '14px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic 
                    title="Avg Order Value"
                    value={salesTypeData.avgOrderValues[index]}
                    precision={2}
                    prefix={<DollarOutlined />}
                    valueStyle={{ fontSize: '14px' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
      </Row>
    </Card>
  );
};

export default SalesTypeChart;
